import React, { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import PoLineItemsEditor from '../../components/material/PoLineItemsEditor';
import { toast } from 'react-toastify';
import { createPurchaseOrder, addPurchaseOrderItem } from '../../api/procurement';
import { getPurchasableVendors } from '../../api/vendor';
import { generateTxnNumber, computePoFinancials } from '../../utils';
import {
  isItemActive,
  inactiveItemMessage,
  formatInactiveItemApiError,
} from '../../utils/itemDisplay';
import { IPD_COLORS } from '../../theme';

const LINE_DEFAULT = {
  item_id: '', uom_id: '', ordered_quantity: '',
  unit_rate: '', tax_percent: '', discount_percent: '',
};

export default function PoForm() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const location = useLocation();
  const preselectedVendorId = useMemo(
    () => new URLSearchParams(location.search).get('vendor_id') || '',
    [location.search],
  );
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [lines, setLines] = useState([{ ...LINE_DEFAULT }]);

  const { control, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: {
      po_number: generateTxnNumber('PO'),
      vendor_id: '',
      expected_delivery_date: dayjs().add(14, 'day').format('YYYY-MM-DD'),
      delivery_address: '',
      payment_terms: '',
      remarks: '',
    },
  });

  const financials = useMemo(() => computePoFinancials(lines), [lines]);

  useEffect(() => {
    setVendorsLoading(true);
    getPurchasableVendors()
      .then((list) => {
        setVendors(list);
        if (preselectedVendorId && list.some((v) => (v.vendor_id || v.id) === preselectedVendorId)) {
          setValue('vendor_id', preselectedVendorId);
        }
      })
      .catch(() => setVendors([]))
      .finally(() => setVendorsLoading(false));
  }, [preselectedVendorId, setValue]);

  function addLine(afterIndex) {
    setLines(l => {
      const row = { ...LINE_DEFAULT };
      if (afterIndex === undefined) return [...l, row];
      const next = [...l];
      next.splice(afterIndex + 1, 0, row);
      return next;
    });
  }

  function removeLine(i) {
    setLines(l => (l.length <= 1 ? l : l.filter((_, idx) => idx !== i)));
  }

  function updateLine(i, key, val, item) {
    setLines(l => l.map((row, idx) => {
      if (idx !== i) return row;
      const updated = { ...row, [key]: val };
      if (key === 'item_id' && item) {
        updated.uom_id = item.base_uom_id || '';
        updated.item_name = item.item_name || '';
        updated.is_active = item.is_active;
        // Tax Master-derived GST auto-fills tax_percent when the line hasn't set one yet.
        if (item.gst_rate != null && !Number(row.tax_percent)) {
          updated.tax_percent = item.gst_rate;
        }
      }
      return updated;
    }));
  }

  async function onSubmit(data) {
    if (financials.lineCount === 0) return;

    const inactiveLine = lines.find(l => l.item_id && !isItemActive(l));
    if (inactiveLine) {
      toast.error(inactiveItemMessage(inactiveLine));
      return;
    }

    const validLines = lines.filter(l => l.item_id && l.ordered_quantity && l.unit_rate);
    setSaving(true);
    try {
      const po = await createPurchaseOrder({ ...data });
      const poId = po.purchase_order_id || po.id;
      await Promise.all(
        validLines.map(l =>
          addPurchaseOrderItem(poId, {
            item_id: l.item_id,
            uom_id: l.uom_id,
            ordered_quantity: Number(l.ordered_quantity),
            unit_rate: Number(l.unit_rate),
            tax_percent: Number(l.tax_percent) || 0,
            discount_percent: Number(l.discount_percent) || 0,
          })
        )
      );
      history.push(`${url.replace('/po/new', '/po')}/${poId}`);
    } catch (e) {
      const named = validLines.find(l => l.item_name) || validLines[0];
      toast.error(formatInactiveItemApiError(e, named));
    } finally {
      setSaving(false);
    }
  }

  const vendorOptions = vendors.map(v => ({
    value: v.vendor_id || v.id,
    label: v.vendor_code ? `${v.vendor_name} (${v.vendor_code})` : v.vendor_name,
  }));

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <PageHeader
        title="New Purchase Order"
        description="Enter vendor details and line items. Totals update automatically."
        actions={
          <>
            <InvButton variant="outlined" tone="teal" onClick={() => history.goBack()}>Cancel</InvButton>
            <InvButton type="submit" loading={saving} disabled={financials.lineCount === 0}>
              Create PO
            </InvButton>
          </>
        }
      />

      <SectionCard title="PO Header">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Controller name="po_number" control={control} rules={{ required: 'Required' }}
              render={({ field }) => <InvTextField {...field} label="PO Number *" error={errors.po_number} />} />
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <Controller name="vendor_id" control={control} rules={{ required: 'Required' }}
              render={({ field }) => (
                <>
                  <InvSelect label="Vendor *" value={field.value} onChange={field.onChange}
                    options={vendorOptions} error={errors.vendor_id}
                    disabled={vendorsLoading} />
                  {!vendorsLoading && vendorOptions.length === 0 && (
                    <Typography sx={{ fontSize: 12, color: IPD_COLORS.warning.text, mt: 0.5 }}>
                      No approved vendors available. Complete vendor approval workflow first.
                    </Typography>
                  )}
                </>
              )} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Controller name="expected_delivery_date" control={control}
              render={({ field }) => (
                <InvTextField {...field} label="Expected Delivery" type="date" InputLabelProps={{ shrink: true }} />
              )} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Controller name="payment_terms" control={control}
              render={({ field }) => <InvTextField {...field} label="Payment Terms" placeholder="e.g. Net 30" />} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller name="delivery_address" control={control}
              render={({ field }) => <InvTextField {...field} label="Delivery Address" multiline rows={2} />} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller name="remarks" control={control}
              render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />} />
          </Grid>
        </Grid>
      </SectionCard>

      <Box sx={{ mt: 0.5 }}>
        <PoLineItemsEditor
          lines={lines}
          onAddLine={addLine}
          onRemoveLine={removeLine}
          onUpdateLine={updateLine}
        />
      </Box>
    </Box>
  );
}
